import React from "react";
import {
 Card,
 H1,
 H3,
 Text,
 Button,
 ControlGroup,
 InputGroup,
 Select,
 HTMLSelect,
 Tag,
 Spinner,
 NonIdealState,
 Divider,
 Icon,
 ButtonGroup,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import {
 getStatusMeta as getStatusMetaGlobal,
 getDisplayStatusMeta as getDisplayStatusMetaGlobal,
 toTitleCase as toTitleCaseGlobal,
} from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";
import cogoToast from "cogo-toast";
import moment from "moment";
import _ from "lodash";
import { withRouter } from "react-router-dom";

class PostponedRegister extends React.Component {
 state = {
  // Data
  appointments: [],
  branches: [],
  doctors: [],

  // Loading states
  isLoading: false,
  isGenerating: false,

  // Filters
  fromDate: null,
  toDate: null,
  selectedBranch: "",
  selectedDoctor: "",
  selectedBranchName: null,
  selectedDoctorName: null,
  dateFilterApplied: false, // Track if date filter should be applied

  // Report data
  reportData: [], // Original unfiltered data from API
  allAppointments: [], // All appointments data for client-side filtering
  groupedData: {},

  // AG Grid state
  gridApi: null,
  gridColumnApi: null,
  quickFilterText: "",
 };

 getPageTitle = () => "Postponed Surgery Register";

 getReportHeading = () =>
  `${this.getPageTitle().toUpperCase()} - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}`;

 getStatusFilters = () => ["postponed"];

 filterAppointmentsByStatus = (appointments = []) => {
  const filters = this.getStatusFilters();
  if (!filters.length) return appointments;
  return appointments.filter((appointment) =>
   filters.includes(String(appointment.status || "").toLowerCase())
  );
 };

 componentDidMount = async () => {
  await this.loadInitialData();
 };

 componentDidUpdate(prevProps) {
  if (
   prevProps.authStore?.selectedBranchName !==
   this.props.authStore?.selectedBranchName
  ) {
   const preferredName = this.props.authStore?.selectedBranchName;
   if (!preferredName) {
    this.setState({
     selectedBranch: "",
     selectedBranchName: null,
    });
    return;
   }
   const match = (this.state.branches || []).find(
    (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
   );
   if (match && match._id !== this.state.selectedBranch) {
    this.setState({
     selectedBranch: match._id,
     selectedBranchName: match.name,
    });
   }
  }
 }

 loadInitialData = async () => {
  try {
   this.setState({ isLoading: true });

   // Load branches and doctors in parallel
   const [branchesResponse, doctorsResponse] = await Promise.all([
    this.loadBranches(),
    this.loadDoctors(),
   ]);

   // Default branch from global selection if available
   if (Array.isArray(this.state.branches) && this.state.branches.length) {
    const preferredName = this.props.authStore?.selectedBranchName;
    const norm = (s) =>
     String(s || "")
      .trim()
      .toLowerCase();
    const match = preferredName
     ? this.state.branches.find((b) => norm(b.name) === norm(preferredName))
     : null;
    if (match)
     this.setState({
      selectedBranch: match._id,
      selectedBranchName: match.name,
     });
   }

   // Load all postponed surgeries without date filter by default
   await this.loadAllPostponedSurgeries();
  } catch (err) {
   console.error("Error loading initial data:", err);
   // Fallback to dummy data if API fails
   this.loadDummyData();
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadBranches = async () => {
  try {
   // Use the existing branch master API
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllSites`,
    {}
   );

   // Normalize different response structures to an array of {_id, name}
   const rawBranches = response?.data || response || [];
   const branches = (Array.isArray(rawBranches) ? rawBranches : [])
    .map((site, idx) => ({
     _id:
      site?._id ||
      site?.id ||
      site?.siteId ||
      site?.value ||
      site?.key ||
      (typeof site === "string" ? site : String(idx)),
     name:
      site?.site ||
      site?.siteName ||
      site?.name ||
      site?.branchName ||
      site?.label ||
      (typeof site === "string" ? site : `Site ${idx + 1}`),
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

   console.log("🔍 DEBUG: Branches loaded:", branches.length);
   this.setState({ branches });
   return branches;
  } catch (err) {
   console.error("Error loading branches:", err);
   // Try alternative API endpoint
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`,
     {}
    );
    const rawBranches = response?.data || response || [];
    const branches = (Array.isArray(rawBranches) ? rawBranches : [])
     .map((site, idx) => ({
      _id: site?._id || site?.id || String(idx),
      name:
       site?.site ||
       site?.siteName ||
       site?.name ||
       site?.branchName ||
       site?.label ||
       `Site ${idx + 1}`,
     }))
     .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    this.setState({ branches });
    return branches;
   } catch (fallbackErr) {
    console.error("Fallback branch API also failed:", fallbackErr);
    // Return dummy branches if both APIs fail
    const dummyBranches = [
     { _id: "branch1", name: "Barrackpore" },
     { _id: "branch2", name: "Kolkata" },
     { _id: "branch3", name: "Howrah" },
     { _id: "branch4", name: "Dum Dum" },
    ];
    this.setState({ branches: dummyBranches });
    return dummyBranches;
   }
  }
 };

 loadDoctors = async () => {
  try {
   // Use the existing doctor/consultant API
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );

   // Transform consultants to doctor format
   const doctors = Array.isArray(response)
    ? response.map((consultant) => ({
       _id: consultant._id || consultant.id,
       name: consultant.name || consultant.consultantName,
       specialization: consultant.specialization || consultant.department,
       phone: consultant.phone || consultant.contactNumber,
       email: consultant.email || consultant.emailId,
       shortcode: consultant.shortcode || consultant.code,
      }))
    : [];

   console.log("🔍 DEBUG: Doctors/Consultants loaded:", doctors.length);
   this.setState({ doctors });
   return doctors;
  } catch (err) {
   console.error("Error loading doctors:", err);
   // Try alternative API endpoint
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`,
     {}
    );
    const doctors = response?.data || response || [];
    this.setState({ doctors });
    return doctors;
   } catch (fallbackErr) {
    console.error("Fallback doctor API also failed:", fallbackErr);
    // Return dummy doctors if both APIs fail
    const dummyDoctors = [
     { _id: "doc1", name: "DR. ARIJIT MITRA" },
     { _id: "doc2", name: "DR. ARNAB DAS" },
     { _id: "doc3", name: "DR. SUDIPTA ROY" },
     { _id: "doc4", name: "DR. PRITAM SARKAR" },
    ];
    this.setState({ doctors: dummyDoctors });
    return dummyDoctors;
   }
  }
 };

 // Load all postponed surgeries without date filter
 loadAllPostponedSurgeries = async () => {
  try {
   this.setState({ isGenerating: true });

   const payload = {
    page: 1,
    sortBy: "scheduledDate",
    sortOrder: "asc",
    // No date filters - load all postponed surgeries
   };

   // Add filters if selected
   if (this.state.selectedBranch) {
    console.log("this.state,", this.state);
    payload.branchName = this.state.selectedBranch;
   }

   payload.status = "postponed";

   // Note: Doctor filtering is now handled client-side for better UX
   // Only apply server-side filtering for branch (no date filter by default)
   console.log("🔍 DEBUG: API filters applied:", {
    dateRange: "All dates (no filter)",
    branchFilter: this.state.selectedBranch || "All branches",
    statusFilter: "postponed",
    doctorFilterHandledClientSide: true,
   });

   // Use the existing getSurgeries API
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
    payload
   );

   // Handle the API response structure properly
   const surgeries = response?.data?.items
    ? response.data.items
    : response?.data || response || [];

   console.log("🔍 DEBUG: Booking Register API Response:", response);
   console.log("🔍 DEBUG: Surgeries loaded for report:", surgeries.length);

   // Debug: Check surgery data structure for selectedSurgeries
   if (surgeries.length > 0) {
    console.log("🔍 DEBUG: Sample surgery data structure:", surgeries[0]);
    console.log(
     "🔍 DEBUG: Selected surgeries in first record:",
     surgeries[0]?.selectedSurgeries
    );
   }

   // Transform the surgery data to match appointment register format
   const transformedAppointments =
    this.transformSurgeriesToAppointments(surgeries);

   // Debug: Check doctor names in transformed data
   if (this.state.selectedDoctor && transformedAppointments.length > 0) {
    console.log("🔍 DEBUG: Doctor names in transformed data:", {
     selectedDoctorFilter: this.state.selectedDoctorName,
     extractedName: this.extractDoctorNameFromFormatted(
      this.state.selectedDoctorName
     ),
     doctorNamesInData: [
      ...new Set(transformedAppointments.map((apt) => apt.doctorName)),
     ],
     sampleAppointment: transformedAppointments[0],
    });
   }

   const statusFilteredAppointments = this.filterAppointmentsByStatus(
    transformedAppointments
   );

   // Store all appointments for client-side filtering
   this.setState(
    {
     allAppointments: statusFilteredAppointments,
     dateFilterApplied: false, // No date filter applied
    },
    () => {
     // Apply client-side filters after storing all data
     this.applyClientSideFilters();
    }
   );

   cogoToast.success(`Loaded ${statusFilteredAppointments.length} postponed surgeries (all dates)`);
  } catch (err) {
   console.error("Error loading postponed surgeries:", err);
   cogoToast.error("Failed to load postponed surgeries. Using sample data.");
   // Fallback to dummy data if API fails
   this.loadDummyAppointments();
  } finally {
   this.setState({ isGenerating: false });
  }
 };

 // Load appointments with date filter (called when user clicks Generate)
 loadAppointments = async () => {
  try {
   this.setState({ isGenerating: true });

   // Only apply date filter if dates are selected
   if (!this.state.fromDate || !this.state.toDate) {
    cogoToast.warn("Please select both From Date and To Date to apply date filter");
    this.setState({ isGenerating: false });
    return;
   }

  const payload = {
   page: 1,
   sortBy: "scheduledDate",
   sortOrder: "asc",
   startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
   endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
   typeOfDate: "scheduledDate", // Specify which date field to filter by
  };

  // Add filters if selected
  if (this.state.selectedBranch) {
   console.log("this.state,", this.state);
   payload.branchName = this.state.selectedBranch;
  }

  payload.status = "postponed";

  // Note: Doctor filtering is now handled client-side for better UX
  // Only apply server-side filtering for date and branch
  console.log("🔍 DEBUG: API filters applied:", {
   dateRange: `${payload.startDate} to ${payload.endDate}`,
   branchFilter: this.state.selectedBranch || "All branches",
   statusFilter: "postponed",
   doctorFilterHandledClientSide: true,
  });

  // Use the existing getSurgeries API
  const response = await apiCallPost(
   `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
   payload
  );

  // Handle the API response structure properly
  const surgeries = response?.data?.items
   ? response.data.items
   : response?.data || response || [];

  console.log("🔍 DEBUG: Postponed Register API Response:", response);
  console.log("🔍 DEBUG: Surgeries loaded for report:", surgeries.length);

  // Debug: Check surgery data structure for selectedSurgeries
  if (surgeries.length > 0) {
   console.log("🔍 DEBUG: Sample surgery data structure:", surgeries[0]);
   console.log(
    "🔍 DEBUG: Selected surgeries in first record:",
    surgeries[0]?.selectedSurgeries
   );
  }

  // Transform the surgery data to match appointment register format
  const transformedAppointments =
   this.transformSurgeriesToAppointments(surgeries);

  // Debug: Check doctor names in transformed data
  if (this.state.selectedDoctor && transformedAppointments.length > 0) {
   console.log("🔍 DEBUG: Doctor names in transformed data:", {
    selectedDoctorFilter: this.state.selectedDoctorName,
    extractedName: this.extractDoctorNameFromFormatted(
     this.state.selectedDoctorName
    ),
    doctorNamesInData: [
     ...new Set(transformedAppointments.map((apt) => apt.doctorName)),
    ],
    sampleAppointment: transformedAppointments[0],
   });
  }

  const statusFilteredAppointments = this.filterAppointmentsByStatus(
   transformedAppointments
  );

  // Store all appointments for client-side filtering
  this.setState(
   {
    allAppointments: statusFilteredAppointments,
    dateFilterApplied: true, // Mark that date filter has been applied
   },
   () => {
    // Apply client-side filters after storing all data
    this.applyClientSideFilters();
   }
  );

  cogoToast.success(`Loaded ${statusFilteredAppointments.length} postponed surgeries with date filter applied`);
 } catch (err) {
  console.error("Error loading appointments:", err);
  cogoToast.error("Failed to load appointments. Using sample data.");
  // Fallback to dummy data if API fails
  this.loadDummyAppointments();
 } finally {
  this.setState({ isGenerating: false });
 }
};

 // Helper function to calculate age from date of birth
 calculateAge = (dob) => {
  if (!dob) return null;
  try {
   const birthDate = new Date(dob);
   const today = new Date();
   let age = today.getFullYear() - birthDate.getFullYear();
   const monthDiff = today.getMonth() - birthDate.getMonth();

   if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
   ) {
    age--;
   }

   return age;
  } catch (err) {
   console.error("Error calculating age:", err);
   return null;
  }
 };

 // Helper function to create branch items for dropdown
 createBranchSelectItems = () => {
  return this.state.branches.map((branch) => branch.name).filter(Boolean);
 };

 // Helper function to create doctor items for dropdown with shortcode formatting
 createDoctorSelectItems = () => {
  return this.state.doctors
   .map((doctor) => {
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
   })
   .filter(Boolean);
 };

 // Helper function to get formatted doctor name
 getFormattedDoctorName = (doctor) => {
  if (!doctor) return null;
  const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
  const name = doctor.name;
  return `${shortcode}${name}`;
 };

 // Helper function to extract doctor name from formatted string
 extractDoctorNameFromFormatted = (formattedName) => {
  if (!formattedName) return null;
  // Remove shortcode part (everything before " | ")
  const withoutShortcode = formattedName.includes(" | ")
   ? formattedName.split(" | ")[1]
   : formattedName;
  return withoutShortcode.trim();
 };

 // Apply client-side filtering to existing data
 applyClientSideFilters = () => {
  let filteredAppointments = [...this.state.allAppointments];

  // Apply doctor filter if selected
  if (this.state.selectedDoctor && this.state.selectedDoctorName) {
   const extractedDoctorName = this.extractDoctorNameFromFormatted(
    this.state.selectedDoctorName
   );
   console.log(
    "🔍 DEBUG: Applying client-side doctor filter:",
    extractedDoctorName
   );

   filteredAppointments = filteredAppointments.filter((appointment) => {
    const appointmentDoctorName = appointment.doctorName || "";
    // Try multiple matching strategies
    const matches = [
     // Exact match
     appointmentDoctorName === extractedDoctorName,
     // Match without "DR. " prefix
     appointmentDoctorName.replace(/^DR\.\s*/i, "") ===
      extractedDoctorName.replace(/^DR\.\s*/i, ""),
     // Case-insensitive match
     appointmentDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
     // Partial match (contains)
     appointmentDoctorName
      .toLowerCase()
      .includes(extractedDoctorName.toLowerCase()),
    ].some(Boolean);

    return matches;
   });

   console.log("🔍 DEBUG: Client-side filtering result:", {
    original: this.state.allAppointments.length,
    filtered: filteredAppointments.length,
    extractedDoctorName,
   });
  }

  // Update state with filtered data
  this.setState({
   appointments: filteredAppointments,
   reportData: filteredAppointments,
  });

  this.groupDataByDoctor(filteredAppointments);

  // Show feedback to user
  const filterMsg = this.state.selectedDoctorName
   ? `Filtered to ${
      filteredAppointments.length
     } appointments for ${this.extractDoctorNameFromFormatted(
      this.state.selectedDoctorName
     )}`
   : `Showing all ${filteredAppointments.length} appointments`;

  cogoToast.success(filterMsg);
 };

 // Helper function to format selected surgeries
 formatSelectedSurgeries = (selectedSurgeries) => {
  if (
   !selectedSurgeries ||
   !Array.isArray(selectedSurgeries) ||
   selectedSurgeries.length === 0
  ) {
   return null;
  }

  return selectedSurgeries
   .map((surgery) => {
    let surgeryDisplay = surgery.name || "Unknown Surgery";

    // Optionally add pricing information in parentheses
    // if (surgery.pricing && surgery.pricing.surgeryRate > 0) {
    //   surgeryDisplay += ` (₹${surgery.pricing.surgeryRate.toLocaleString()})`;
    // }

    return surgeryDisplay;
   })
   .join("; ");
 };

 formatSelectedGroup = (selectedSurgeries) => {
  if (
   !selectedSurgeries ||
   !Array.isArray(selectedSurgeries) ||
   selectedSurgeries.length === 0
  ) {
   return null;
  }

  return selectedSurgeries.map((surgery) => surgery.group || "").join(", ");
 };

 toTitleCase = (value) => toTitleCaseGlobal(value);

 getStatusMeta = (status) => getStatusMetaGlobal(status);

 getDisplayStatusMeta = (row) => {
  const status = row?.status || "-";
  const payorPanel = row?.payorPanel || row?.payor || row?.patientPanel || "";
  const isBilled = row?.isBilled ?? false;
  return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
 };

 renderStatusCell = (row) => {
  if (!row) {
   return "-";
  }
  const meta = this.getDisplayStatusMeta(row);
  return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
 };
 renderIsBilledCell = (row) => getIsBilledPill(row);
 // Helper function to format IOL items with variants
 formatIOLItems = (iolItems) => {
  if (!iolItems || !Array.isArray(iolItems) || iolItems.length === 0) {
   return null;
  }

  return iolItems
   .map((item) => {
    let itemDisplay = item.itemName || "Unknown IOL";

    // Add variants if they exist
    if (
     item.hasVariants &&
     item.selectedVariants &&
     Object.keys(item.selectedVariants).length > 0
    ) {
     const variants = [];

     // Format variants with proper names
     Object.entries(item.selectedVariants).forEach(([key, value]) => {
      if (value) {
       // Get the proper variant name from itemDetails
       let variantName = key;
       if (key === "property1" && item.itemDetails?.variantProperty1Name) {
        variantName = item.itemDetails.variantProperty1Name;
       } else if (
        key === "property2" &&
        item.itemDetails?.variantProperty2Name
       ) {
        variantName = item.itemDetails.variantProperty2Name;
       } else if (
        key === "property3" &&
        item.itemDetails?.variantProperty3Name
       ) {
        variantName = item.itemDetails.variantProperty3Name;
       }

       variants.push(`${variantName}: ${value}`);
      }
     });

     if (variants.length > 0) {
      itemDisplay += ` (${variants.join(", ")})`;
     }
    }

    return itemDisplay;
   })
   .join("; ");
 };

 // Navigate to surgery details page
 navigateToDetails = (surgery) => {
  const detailsPath = `/v3/workflow/surgery-booking/${
   surgery._id || surgery.docNo || surgery.bookingReference || surgery.surgeryId
  }`;
  window.open(detailsPath, "_blank", "noopener,noreferrer");
 };

 // AG Grid column definitions
 getColumnDefs = () => [
  {
   headerName: "Surgery ID",
   field: "docNo",
   width: 120,
   filter: "agTextColumnFilter",
   valueGetter: (params) => {
    return (
     params.data.docNo || params.data.bookingId || params.data.surgeryId || "-"
    );
   },
   cellRenderer: (params) => {
    const value =
     params.data.docNo || params.data.bookingId || params.data.surgeryId;
    if (!value || value === "-") return "-";

    const container = document.createElement("div");
    const link = document.createElement("span");

    link.innerHTML = value;
    link.style.color = "#137cbd";
    link.style.cursor = "pointer";
    link.style.textDecoration = "underline";
    link.style.fontWeight = "600";
    link.onclick = () => this.navigateToDetails(params.data);

    container.appendChild(link);
    return container;
   },
  },
  {
   headerName: "PID/Patient Name",
   field: "patientInfo",
   width: 200,
   filter: "agTextColumnFilter",
   valueGetter: (params) => {
    const pid = params.data.patientId || params.data.pid || "-";
    const name = params.data.patientName || "-";
    return `${pid}/${name}`;
   },
   cellRenderer: (params) => {
    const pid = params.data.patientId || params.data.pid || "-";
    const name = params.data.patientName || "-";

    return `
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">${pid} / ${name}</div>
          </div>
        `;
   },
  },
  {
   headerName: "Patient Phone",
   field: "patientPhone",
   width: 140,
   filter: "agTextColumnFilter",
   valueGetter: (params) => {
    return params.data.patientPhone || "-";
   },
   cellRenderer: (params) => {
    const phone = params.data.patientPhone || "-";
    return `
          <div style="text-align: center;">
            <div style="font-weight: 500;">${phone}</div>
          </div>
        `;
   },
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Original Date",
   field: "scheduledDate",
   width: 140,
   filter: "agDateColumnFilter",
   valueGetter: (params) => {
    return params.data.scheduledDate
     ? moment(params.data.scheduledDate).format("DD/MM/YYYY")
     : "-";
   },
   cellRenderer: (params) => {
    const date = params.data.scheduledDate
     ? moment(params.data.scheduledDate).format("DD/MM/YYYY")
     : "-";
    const time = params.data.scheduledTime || "";

    return `
          <div style="text-align: center;">
            <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
            ${
             time
              ? `<div style="font-size: 12px; color: #8A9BA8;">${time}</div>`
              : ""
            }
          </div>
        `;
   },
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Status",
   field: "status",
   width: 130,
   filter: "agSetColumnFilter",
   filterValueGetter: (params) =>
    this.getDisplayStatusMeta(params.data || {}).label,
   valueFormatter: (params) =>
    this.getDisplayStatusMeta(params.data || {}).label,
   cellRenderer: (params) => this.renderStatusCell(params.data),
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Is Billed",
   field: "isBilled",
   width: 120,
   filter: "agSetColumnFilter",
   valueGetter: (params) => getIsBilledLabel(params.data),
   valueFormatter: (params) => getIsBilledLabel(params.data),
   cellRenderer: (params) => this.renderIsBilledCell(params.data),
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Doctor/Surgeon",
   field: "doctorName",
   width: 160,
   filter: "agTextColumnFilter",
   valueFormatter: (params) => {
    return params.value ? params.value.replace("DR. ", "") : "-";
   },
   cellStyle: { fontWeight: "600" },
  },
  {
   headerName: "Age",
   field: "patientAge",
   width: 80,
   filter: "agNumberColumnFilter",
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Sex",
   field: "patientGender",
   width: 80,
   filter: "agTextColumnFilter",
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Eye",
   field: "eye",
   width: 80,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const eyeColor = params.value === "RE" ? "#137CBD" : "#0F9960";
    return `<span style="background: ${eyeColor}20; color: ${eyeColor}; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 500;">${
     params.value || "-"
    }</span>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Diet",
   field: "diet",
   width: 120,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const isDiabetic = params.value === "DIABETIC";
    const color = isDiabetic ? "#D9822B" : "#0F9960";
    return `<span style="background: ${color}20; color: ${color}; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 500;">${
     params.value || "-"
    }</span>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "IOL/Implant",
   field: "lens",
   width: 250,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const isNoLens = params.value === "NO LENS";
    const style = isNoLens
     ? "font-style: italic; color: #8A9BA8;"
     : "color: #F5F8FA;";
    return `<div style="${style} line-height: 1.4; word-wrap: break-word; max-width: 250px;">${
     params.value || "NO LENS"
    }</div>`;
   },
  },
  // {
  //  headerName: "Special Note",
  //  field: "specialNote",
  //  width: 150,
  //  filter: "agTextColumnFilter",
  //  cellStyle: { color: "#8A9BA8" },
  // },
  {
   headerName: "Surgery",
   field: "surgeryName",
   width: 300,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const surgery = params.data.surgeryName || params.data.surgery || "-";
    return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 300px;">${surgery}</div>`;
   },
  },
  {
   headerName: "Surgery Group",
   field: "surgeryGroup",
   width: 200,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 200px;">${params.value}</div>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Anesthesia",
   field: "anesthesia",
   width: 150,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const anesthesia = params.value || "-";
    return `<div style="line-height: 1.4; text-align: center; font-weight: 500;">${anesthesia}</div>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Postponed At",
   field: "postponedAt",
   width: 160,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    if (!params.value) return `<div style="line-height: 1.4; text-align: center;">-</div>`;
    const formatted = moment(params.value).format("DD/MM/YYYY hh:mm A");
    return `<div style="line-height: 1.4; text-align: center;">${formatted}</div>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Postponed By",
   field: "postponedBy",
   width: 160,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    return `<div style="line-height: 1.4; text-align: center;">${params.value || "-"}</div>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Postponement Notes",
   field: "postponementNotes",
   width: 400,
   minWidth: 320,
   flex: 3,
   autoHeight: true,
   wrapText: true,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    return `<div style="line-height: 1.4; word-wrap: break-word; white-space: pre-wrap; color: #8A9BA8;">${
     params.value || "-"
    }</div>`;
   },
  },
  {
   headerName: "Remarks",
   field: "remarks",
   width: 200,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 200px; color: #8A9BA8;">${
     params.value || "-"
    }</div>`;
   },
  },
 ];

 // Transform surgery booking data to appointment register format
 transformSurgeriesToAppointments = (surgeries) => {
  return surgeries.map((surgery, index) => {
   // Debug: Check what data is available for surgeryGroup
   if (index === 0) {
    console.log("🔍 DEBUG: Surgery data for first record:", {
     selectedSurgeries: surgery.selectedSurgeries,
     group: surgery.group,
     payor: surgery.payor,
     formatSelectedGroupResult: this.formatSelectedGroup(
      surgery.selectedSurgeries
     ),
    });
   }

   return {
    _id: surgery._id || surgery.id || `surgery_${index}`,
    docNo:
     surgery.bookingReference ||
     surgery._id?.toString() ||
     `BKP25U${String(index).padStart(5, "0")}`,
    surgeryId:
     surgery._id || surgery.id || `SX${String(index + 1).padStart(3, "0")}`,
    patientId:
     surgery.patientGenericPid ||
     surgery.patientId ||
     surgery.patientOriginalData?.sysPid ||
     `P${String(index).padStart(10, "0")}`,
    scheduledDate: surgery.scheduledDate
     ? new Date(surgery.scheduledDate)
     : new Date(),
    scheduledTime: surgery.scheduledTime || "09:00",
    patientName:
     surgery.patientName ||
     surgery.patientOriginalData?.name ||
     "Unknown Patient",
    appointmentDate: surgery.scheduledDate
     ? new Date(surgery.scheduledDate)
     : new Date(),
    originalScheduledDate: surgery.originalScheduledDate
     ? new Date(surgery.originalScheduledDate)
     : surgery.scheduledDate
     ? new Date(surgery.scheduledDate)
     : new Date(),
    originalScheduledTime:
     surgery.originalScheduledTime ||
     surgery.originalTime ||
     surgery.scheduledTime ||
     "09:00",
    surgeryDate: surgery.scheduledDate
     ? new Date(surgery.scheduledDate)
     : new Date(),
    scheduledTime: surgery.scheduledTime || "09:00",
    patientAge:
     surgery.patientAge ||
     this.calculateAge(surgery.patientDob) ||
     this.calculateAge(surgery.patientOriginalData?.dob),
    patientGender:
     surgery.patientSex || surgery.patientOriginalData?.sex || "Unknown",
    patientPhone:
     surgery.patientPhone || surgery.patientOriginalData?.phone || "",
    patientEmail:
     surgery.patientEmail || surgery.patientOriginalData?.email || "",
    patientAddress:
     surgery.patientAddress || surgery.patientOriginalData?.address || "",
    eye: surgery.selectedSurgeries[0]?.laterality || "",
    diet: surgery.isDiabetic ? "DIABETIC" : "NON-DIABETIC",
    lens:
     this.formatIOLItems(surgery.IOLitems) ||
     surgery.iolType ||
     surgery.implantType ||
     "NO LENS",
    specialNote: surgery.specialInstructions || surgery.preOpInstructions || "",
    surgeryName:
     // Use helper method to format selectedSurgeries with pricing
     this.formatSelectedSurgeries(surgery.selectedSurgeries) ||
     // Fallback to other surgery name fields
     (Array.isArray(surgery.surgeryNames) && surgery.surgeryNames.length > 0
      ? surgery.surgeryNames.join(", ")
      : typeof surgery.surgeryNames === "string" && surgery.surgeryNames.trim()
      ? surgery.surgeryNames
      : surgery.surgeryName) ||
     surgery.procedure ||
     surgery.surgeryType ||
     surgery.type ||
     "Surgery",
    surgeryGroup:
     this.formatSelectedGroup(surgery.selectedSurgeries) ||
     surgery.group ||
     surgery.payor ||
     "",
    anesthesia:
     surgery.anesthesia ||
     surgery.anaesthesiaType ||
     surgery.anesthesiaType ||
     "Topical",
    remarks:
     surgery.additionalNotes || surgery.notes || surgery.medicalNotes || "",
    payorPanel: surgery.payorPanel || "",
    patientPanel:
     surgery.payorPanel || surgery.patientOriginalData?.panel || "Cash",
    isBilled: Boolean(surgery.isBilled),
    doctorName: surgery.doctorName || "Unknown Doctor",
    doctorId: surgery.doctorId || surgery.doctor?._id || "",
    branchName: surgery.branchName || "Unknown Branch",
    branchId: surgery.branchId || surgery.branch?._id || "",
    createdByName: surgery.createdByName || surgery.createdBy || "System",
    createdAt: surgery.createdAt || new Date(),
    bookedAt: surgery.createdAt || new Date(),
    status: surgery.status || "confirmed",
    urgencyLevel: surgery.urgencyLevel || "routine",
    anaesthesiaType:
     surgery.anaesthesiaType || surgery.anesthesiaType || "topical",
    estimatedDuration: surgery.estimatedDuration || 60,
    postponementNotes: surgery.postponementNotes || "",
    postponedAt:
        surgery.postponementHistory?.length
            ? surgery.postponementHistory[surgery.postponementHistory.length - 1]?.ts || ""
            : "",
    postponedBy:
        surgery.postponementHistory?.length
            ? surgery.postponementHistory[surgery.postponementHistory.length - 1]?.userName || ""
            : "",
    // Additional fields that might be useful
    whatsappNumber: surgery.whatsappNumber || "",
    enableWhatsappConfirmation: surgery.enableWhatsappConfirmation || false,
    enableEmailConfirmation: surgery.enableEmailConfirmation || false,
    timeFrame: surgery.timeFrame || "morning",
    slotCapacity: surgery.slotCapacity || 1,
    slotBookedCount: surgery.slotBookedCount || 1,
   };
  });
 };

 loadDummyData = () => {
  // Load dummy branches and doctors
  const dummyBranches = [
   { _id: "branch1", name: "Barrackpore" },
   { _id: "branch2", name: "Kolkata" },
   { _id: "branch3", name: "Howrah" },
   { _id: "branch4", name: "Dum Dum" },
  ];

  const dummyDoctors = [
   { _id: "doc1", name: "DR. ARIJIT MITRA" },
   { _id: "doc2", name: "DR. ARNAB DAS" },
   { _id: "doc3", name: "DR. SUDIPTA ROY" },
   { _id: "doc4", name: "DR. PRITAM SARKAR" },
  ];

  this.setState({
   branches: dummyBranches,
   doctors: dummyDoctors,
  });

  // Load dummy appointments
  this.loadDummyAppointments();
 };

 loadDummyAppointments = () => {
  const dummyAppointments = [
   {
    _id: "apt1",
    docNo: "BKP25U00012",
    surgeryId: "SX001",
    patientId: "P0401405502",
    patientName: "RAGHU NATH DAS (SSC)",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 9, minute: 0 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 9, minute: 0 }).toDate(),
    patientAge: 50,
    patientGender: "Male",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "TA INTRAVITREAL RAZUMAB( RANIBIZUMAB)",
    remarks: "PAYING BED HTN",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt2",
    docNo: "BKP25U00013",
    surgeryId: "SX002",
    patientId: "P0401405503",
    patientName: "SUNITA DEVI",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 10, minute: 30 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 10, minute: 30 }).toDate(),
    patientAge: 65,
    patientGender: "Female",
    eye: "LE",
    diet: "DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "LA VITRECTOMY (L1)+MIVS+SOI",
    remarks: "PAYING BED 3000/- LESS.HT.",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt3",
    docNo: "BKP25U00014",
    surgeryId: "SX003",
    patientId: "P0401405504",
    patientName: "AMIT KUMAR SINGH",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 11, minute: 15 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 11, minute: 15 }).toDate(),
    patientAge: 45,
    patientGender: "Male",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName:
     "TA PHACO WITH HYDROPHOBIC J&J ASPHERIC TECNIS 1/ HOYA VIVINEX MULTISERT IOL+VISCOAT+TOPICAL",
    remarks: "PAYING BED WBHS/HT/CHOLES/*",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt4",
    docNo: "BKP25U00015",
    surgeryId: "SX004",
    patientId: "P0401405505",
    patientName: "PRIYA SHARMA",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 14, minute: 0 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 14, minute: 0 }).toDate(),
    patientAge: 38,
    patientGender: "Female",
    eye: "LE",
    diet: "NON-DIABETIC",
    lens: "22.00 T1 TECNIS-1",
    specialNote: "",
    surgeryName:
     "TA PHACO WITH HYDROPHOBIC J&J ASPHERIC TECNIS 1/ HOYA VIVINEX MULTISERT IOL+VISCOAT+TOPICAL",
    remarks: "PAYING BED LE T1 DONE BY DR.AD.PT WNT SAME.",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt5",
    docNo: "BKP25U00016",
    surgeryId: "SX005",
    patientId: "P0401405506",
    patientName: "RAJESH KUMAR",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 15, minute: 30 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 15, minute: 30 }).toDate(),
    patientAge: 55,
    patientGender: "Male",
    eye: "RE",
    diet: "DIABETIC",
    lens: "NO LENS",
    specialNote: "High Risk Patient",
    surgeryName: "TA INTRAVITREAL RAZUMAB( RANIBIZUMAB)",
    remarks: "PAYING BED HTN/DIABETES",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt6",
    docNo: "BKP25U00017",
    surgeryId: "SX006",
    patientId: "P0401405507",
    patientName: "ANITA DEVI",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 16, minute: 0 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 16, minute: 0 }).toDate(),
    patientAge: 42,
    patientGender: "Female",
    eye: "LE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "LA VITRECTOMY (L1)+MIVS+SOI",
    remarks: "PAYING BED",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   {
    _id: "apt7",
    docNo: "BKP25U00018",
    surgeryId: "SX007",
    patientId: "P0401405508",
    patientName: "VIKASH SINGH",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 17, minute: 15 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 17, minute: 15 }).toDate(),
    patientAge: 48,
    patientGender: "Male",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "22.00 T1 TECNIS-1",
    specialNote: "",
    surgeryName:
     "TA PHACO WITH HYDROPHOBIC J&J ASPHERIC TECNIS 1/ HOYA VIVINEX MULTISERT IOL+VISCOAT+TOPICAL",
    remarks: "PAYING BED",
    doctorName: "DR. ARNAB DAS",
    doctorId: "doc2",
   },
   // Add some appointments for other doctors
   {
    _id: "apt8",
    docNo: "BKP25U00019",
    surgeryId: "SX008",
    patientId: "P0401405509",
    patientName: "SURESH KUMAR",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 9, minute: 30 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 9, minute: 30 }).toDate(),
    patientAge: 60,
    patientGender: "Male",
    eye: "LE",
    diet: "DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "TA INTRAVITREAL RAZUMAB( RANIBIZUMAB)",
    remarks: "PAYING BED",
    doctorName: "DR. SUDIPTA ROY",
    doctorId: "doc3",
   },
   {
    _id: "apt9",
    docNo: "BKP25U00020",
    surgeryId: "SX009",
    patientId: "P0401405510",
    patientName: "KAVITA SINGH",
    appointmentDate: moment()
     .add(1, "day")
     .set({ hour: 11, minute: 0 })
     .toDate(),
    surgeryDate: moment().add(1, "day").set({ hour: 11, minute: 0 }).toDate(),
    patientAge: 35,
    patientGender: "Female",
    eye: "RE",
    diet: "NON-DIABETIC",
    lens: "NO LENS",
    specialNote: "",
    surgeryName: "LA VITRECTOMY (L1)+MIVS+SOI",
    remarks: "PAYING BED",
    doctorName: "DR. SUDIPTA ROY",
    doctorId: "doc3",
   },
  ];

  const appointmentsWithOriginals = dummyAppointments.map((appointment) => ({
   ...appointment,
   originalScheduledDate:
    appointment.originalScheduledDate || appointment.surgeryDate,
   originalScheduledTime:
    appointment.originalScheduledTime || appointment.scheduledTime || "",
  }));

  const statusFilteredAppointments = this.filterAppointmentsByStatus(
   appointmentsWithOriginals
  );

  this.setState(
   {
    allAppointments: statusFilteredAppointments,
   },
   () => {
    // Apply client-side filters after storing dummy data
    this.applyClientSideFilters();
   }
  );

  cogoToast.info(
   "Using sample data for demonstration. API integration will be available soon."
  );
 };

 groupDataByDoctor = (data) => {
  // Always show data in a single table without grouping
  this.setState({
   groupedData: { "All Appointments": data },
  });
 };

 // AG Grid event handlers
 onGridReady = (params) => {
  this.setState(
   {
    gridApi: params.api,
    gridColumnApi: params.columnApi,
   },
   () => {
    try {
     // Auto-fit Surgery ID column so booking reference text is fully visible
     if (params?.columnApi?.autoSizeColumns) {
      params.columnApi.autoSizeColumns(["docNo"]);
     }
    } catch (err) {
     console.error("Failed to auto-size Surgery ID column", err);
    }
   }
  );
 };

// Dynamically size rows to fit long postponement notes
getRowHeight = (params) => {
  const baseHeight = 60;
  const notes = params?.data?.postponementNotes || "";
  if (!notes) return baseHeight;
  const approxLines = Math.ceil(notes.length / 70);
  const computedHeight = baseHeight + approxLines * 18;
  return Math.min(computedHeight, 400);
};

 // Get only visible/displayed columns (respects sidebar column selection)
 getDisplayedColumns = () => {
  if (!this.state.gridColumnApi) return this.getColumnDefs();
  const allColumns = this.state.gridColumnApi.getAllColumns();
  if (!allColumns) return this.getColumnDefs();
  const visibleColumns = allColumns.filter((col) => col.isVisible());
  return visibleColumns.map((col) => {
   const colDef = col.getColDef();
   return {
    headerName: colDef.headerName,
    field: colDef.field,
    ...colDef,
   };
  });
 };

 onQuickFilterChanged = (event) => {
  this.setState({ quickFilterText: event.target.value });
 };

 handleDateChange = (field, date) => {
  this.setState({ [field]: date });
 };

 handleFilterChange = (field, value) => {
  this.setState({ [field]: value });
 };

 // Handle branch selection from SingleSelectWrapper
 handleBranchFilterSelect = (branchName) => {
  const selectedBranch = this.state.branches.find(
   (branch) => branch.name === branchName
  );
  this.setState({
   selectedBranch: selectedBranch ? selectedBranch._id : "",
   selectedBranchName: branchName || null,
  });
 };

 // Handle doctor selection from SingleSelectWrapper
 handleDoctorFilterSelect = (formattedDoctorName) => {
  // Find the doctor by matching the formatted name
  const selectedDoctor = this.state.doctors.find((doctor) => {
   const formatted = this.getFormattedDoctorName(doctor);
   return formatted === formattedDoctorName;
  });

  console.log("🔍 DEBUG: Doctor filter selection:", {
   formattedDoctorName,
   selectedDoctor,
   extractedName: this.extractDoctorNameFromFormatted(formattedDoctorName),
  });

  this.setState(
   {
    selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
    selectedDoctorName: formattedDoctorName || null,
   },
   () => {
    // Apply filtering immediately if we have data
    if (this.state.allAppointments.length > 0) {
     this.applyClientSideFilters();
    }
   }
  );
 };

 generateReport = async () => {
  // Only apply date filter if both dates are selected
  if (this.state.fromDate && this.state.toDate) {
   await this.loadAppointments();
  } else {
   // If no dates selected, reload all postponed surgeries
   await this.loadAllPostponedSurgeries();
   this.setState({ dateFilterApplied: false });
  }
 };

 // Helper function to get sorted and filtered data from AG Grid
 getSortedAndFilteredData = () => {
  if (this.state.gridApi) {
   const sortedData = [];
   // Use forEachNodeAfterFilterAndSort to get data in the current sort order
   this.state.gridApi.forEachNodeAfterFilterAndSort((node) => {
    sortedData.push(node.data);
   });
   return sortedData;
  }
  // Fallback to state data if grid API not available
  return this.state.appointments;
 };

 exportToCSV = () => {
  // Get sorted and filtered data
  let filteredData = this.getSortedAndFilteredData();

  // Get only visible columns (respects sidebar column selection)
  let columns = this.getDisplayedColumns();

  // Combine Age and Sex columns for CSV export
  const hasAgeColumn = columns.some((col) => col.field === "patientAge");
  const hasSexColumn = columns.some((col) => col.field === "patientGender");

  if (hasAgeColumn && hasSexColumn) {
   // Remove individual age and sex columns
   columns = columns.filter(
    (col) => col.field !== "patientAge" && col.field !== "patientGender"
   );

   // Find the position where age column was (to insert combined column there)
   const ageColumnIndex = this.getDisplayedColumns().findIndex(
    (col) => col.field === "patientAge"
   );

   // Insert combined Age/Sex column at the age column position
   columns.splice(ageColumnIndex > 0 ? ageColumnIndex : 6, 0, {
    headerName: "Age / Sex",
    field: "ageSex",
   });
  }

  // Combine Anesthesia and Surgery columns for CSV export
  const hasAnesthesiaColumn = columns.some((col) => col.field === "anesthesia");
  const hasSurgeryColumn = columns.some((col) => col.field === "surgeryName");

  if (hasAnesthesiaColumn && hasSurgeryColumn) {
   // Remove individual anesthesia column (keep surgery column)
   columns = columns.filter((col) => col.field !== "anesthesia");
  }

  // Add serial number column at the start
  columns.unshift({
   headerName: "Sr. No.",
   field: "serialNumber",
  });

  const selectedBranchName =
   this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name ||
   "All Branches";

  // Get selected doctor name if filter is applied
  const selectedDoctorName = this.state.selectedDoctor
   ? this.state.doctors.find((d) => d._id === this.state.selectedDoctor)
      ?.name || "Unknown Doctor"
   : "All Doctors";

  // Create comprehensive header information
  const headerInfo = [
   [this.getReportHeading()],
   [""], // Empty row for spacing
   ["Report Details:"],
   [`Branch: ${selectedBranchName}`],
   [`Doctor/Surgeon: ${selectedDoctorName}`],
   [
    this.state.dateFilterApplied && this.state.fromDate && this.state.toDate
     ? `Filter Period: ${moment(this.state.fromDate).format(
        "DD-MMM-YYYY"
       )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")}`
     : "Filter Period: All dates (no date filter applied)",
   ],
   [`Generated At: ${moment().format("DD-MMM-YYYY, HH:mm:ss")}`],
   [`Total Records: ${filteredData.length}`],
   [""], // Empty row for spacing
   ["Data Export:"],
   [""], // Empty row for spacing
  ];

  // Create column headers from visible columns
  const headers = columns.map((col) => col.headerName);

  // Format data rows based on visible columns
  const dataRows = filteredData.map((appointment, index) => {
   return columns.map((col) => {
    const field = col.field;

    // Handle serial number
    if (field === "serialNumber") {
     return index + 1;
    }

    // Handle special field mappings
    if (field === "doctorName") {
     return appointment.doctorName
      ? appointment.doctorName.replace("DR. ", "")
      : "-";
    }
    if (field === "docNo") {
     return (
      appointment.docNo || appointment.bookingId || appointment.surgeryId || "-"
     );
    }
    if (field === "patientInfo") {
     const pid = appointment.patientId || appointment.pid || "-";
     const name = appointment.patientName || "-";
     return `${pid}/${name}`;
    }
    if (field === "originalScheduledDate") {
     return appointment.originalScheduledDate
      ? `${moment(appointment.originalScheduledDate).format("DD/MM/YYYY")}${
         appointment.originalScheduledTime
          ? ` ${appointment.originalScheduledTime}`
          : ""
        }`
      : "-";
    }
    if (field === "status") {
     return this.getDisplayStatusMeta(appointment).label;
    }
    if (field === "ageSex") {
     const age = appointment.patientAge || "-";
     const sex = appointment.patientGender || "-";
     return `${age} / ${sex}`;
    }
    if (field === "lens") {
     return appointment.lens || "NO LENS";
    }
    if (field === "surgeryName") {
     const anesthesia = appointment.anesthesia || "-";
     const surgery = appointment.surgeryName || appointment.surgery || "-";
     return `${anesthesia} | ${surgery}`;
    }

    if (field === "postponedAt") {
     return appointment.postponedAt
      ? moment(appointment.postponedAt).format("DD/MM/YYYY hh:mm A")
      : "-";
    }

    // Default: return field value or dash
    return appointment[field] || "-";
   });
  });

  // Combine all data
  const csvData = [...headerInfo, headers, ...dataRows];

  // Convert to CSV format
  const csvContent = csvData
   .map((row) =>
    row
     .map((field) => {
      // Escape quotes and wrap in quotes if field contains comma, quote, or newline
      const fieldStr = String(field || "");
      if (
       fieldStr.includes(",") ||
       fieldStr.includes('"') ||
       fieldStr.includes("\n")
      ) {
       return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
     })
     .join(",")
   )
   .join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);

  const fileName = this.state.dateFilterApplied && this.state.fromDate && this.state.toDate
   ? `Postponed_Surgery_Register_${moment(this.state.fromDate).format(
      "DD-MMM-YYYY"
     )}_to_${moment(this.state.toDate).format(
      "DD-MMM-YYYY"
     )}_Generated_${moment().format("DD-MMM-YYYY_HH-mm")}.csv`
   : `Postponed_Surgery_Register_All_Dates_Generated_${moment().format(
      "DD-MMM-YYYY_HH-mm"
     )}.csv`;
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  cogoToast.success(
   `CSV exported successfully: ${fileName} (with selected columns)`
  );
 };

 exportToPDF = () => {
  try {
   // Get sorted and filtered data from AG Grid
   let filteredData = this.getSortedAndFilteredData();

   // Filter out cancelled appointments for export
   filteredData = filteredData.filter((appointment) => {
    const status = String(appointment.status || "").toLowerCase();
    return status !== "cancelled";
   });

   // Get only visible columns (respects sidebar column selection)
   let columns = this.getDisplayedColumns();

   // Combine Age and Sex columns for print preview
   const hasAgeColumn = columns.some((col) => col.field === "patientAge");
   const hasSexColumn = columns.some((col) => col.field === "patientGender");

   if (hasAgeColumn && hasSexColumn) {
    // Remove individual age and sex columns
    columns = columns.filter(
     (col) => col.field !== "patientAge" && col.field !== "patientGender"
    );

    // Find the position where age column was (to insert combined column there)
    const ageColumnIndex = this.getDisplayedColumns().findIndex(
     (col) => col.field === "patientAge"
    );

    // Insert combined Age/Sex column at the age column position
    columns.splice(ageColumnIndex > 0 ? ageColumnIndex : 6, 0, {
     headerName: "Age / Sex",
     field: "ageSex",
    });
   }

   // Combine Anesthesia and Surgery columns for print preview
   const hasAnesthesiaColumn = columns.some(
    (col) => col.field === "anesthesia"
   );
   const hasSurgeryColumn = columns.some((col) => col.field === "surgeryName");

   if (hasAnesthesiaColumn && hasSurgeryColumn) {
    // Remove individual anesthesia column (keep surgery column)
    columns = columns.filter((col) => col.field !== "anesthesia");
   }

   // Add serial number column at the start
   columns.unshift({
    headerName: "Sr. No.",
    field: "serialNumber",
   });

   const selectedBranchName =
    this.state.branches.find((b) => b._id === this.state.selectedBranch)
     ?.name || "All Branches";
   const selectedDoctorName = this.state.selectedDoctorName
    ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
    : null;

   // Create a new window for printing
   const printWindow = window.open("", "_blank");

   // Generate HTML content for PDF
   const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
     <title>Postponed Appointment Register</title>
     <style>
    //  FIXME
      /* Ensure landscape orientation when printing */
      @page {
       size: A4 landscape;
       margin: 8mm;
      }
      //  FIXME
      body { 
       font-family: Arial, sans-serif; 
       margin: 10px; 
       font-size: 14px;
       color: #000;
      }
      .header {
       text-align: center;
       margin-bottom: 30px;
       border-bottom: 2px solid #000;
       padding-bottom: 20px;
      }
      .header h1 {
       margin: 0;
       font-size: 20px;
       font-weight: bold;
      }
      .header p {
       margin: 5px 0;
       font-size: 16px;
      }
      table {
       width: 100%;
       border-collapse: collapse;
       margin-top: 20px;
       font-size: 10px;
      }
      th, td {
       border: 1px solid #000;
       text-align: left;
       vertical-align: top; /* keep content pinned to the top */
       padding: 4px 6px;
       word-wrap: break-word;
       overflow-wrap: break-word;
      }
      th { 
       text-align: center;
       font-weight: bold;
       background-color: #f0f0f0;
      }
      td { 
       line-height: 1.4;
       max-width: 200px; /* default max width */
      }
      tbody tr { page-break-inside: avoid; }
      .surgery-separator {
       border-top: 1px solid #ccc;
       margin: 4px 0;
       padding-top: 4px;
      }
      .anesthesia-text {
       font-weight: 500;
       color: #333;
       margin-bottom: 4px;
      }
      .no-break {
       page-break-inside: avoid;
      }
      @media print {
      //  FIXME
       /* Redefine to reinforce landscape in some browsers */
       @page { size: A4 landscape; margin: 8mm; }
      //  FIXME
       body { margin: 5px; }
       .header { page-break-after: avoid; }
      }
     </style>
    </head>
    <body>
     <div class="header">
      <h1>POSTPONED SURGERY APPOINTMENT REGISTER - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}</h1>
      <p>${selectedBranchName}${
    selectedDoctorName ? ` • ${selectedDoctorName}` : ""
   } - ${moment().format("DD/MM/YYYY, HH:mm")}</p>
      ${
       this.state.dateFilterApplied && this.state.fromDate && this.state.toDate
        ? `<p>Surgeries scheduled for ${moment(this.state.fromDate).format(
           "DD-MMM-YYYY"
          )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")}</p>`
        : `<p>Showing all postponed surgeries (no date filter applied)</p>`
      }
      <p>Total Appointments: ${filteredData.length}</p>
     </div>
     
     <table>
      <thead>
       <tr>
        ${columns.map((col) => `<th>${col.headerName}</th>`).join("")}
       </tr>
      </thead>
      <tbody>
       ${filteredData
        .map((appointment, index) => {
         const cells = columns
          .map((col) => {
           const field = col.field;

           // Handle serial number
           if (field === "serialNumber") {
            return `<td style="max-width: 40px; text-align: center;"><strong>${
             index + 1
            }</strong></td>`;
           }

           // Handle special field mappings
           if (field === "doctorName") {
            return `<td style="max-width: 80px;">${
             appointment.doctorName
              ? appointment.doctorName.replace("DR. ", "")
              : "-"
            }</td>`;
           }
           if (field === "docNo") {
            return `<td style="max-width: 80px;">${
             appointment.docNo ||
             appointment.bookingId ||
             appointment.surgeryId ||
             "-"
            }</td>`;
           }
           if (field === "patientInfo") {
            return `<td style="max-width: 80px;"><strong>${
             appointment.patientId || appointment.pid || "-"
            }/<br>${appointment.patientName || "-"}</strong></td>`;
           }
           if (field === "patientPhone") {
            return `<td style="max-width: 60px;">${
             appointment.patientPhone || "-"
            }</td>`;
           }
           if (field === "originalScheduledDate") {
            return `<td style="max-width: 60px;">${
             appointment.originalScheduledDate
              ? `${moment(appointment.originalScheduledDate).format(
                 "DD/MM/YY"
                )}${
                 appointment.originalScheduledTime
                  ? `<br><small>${appointment.originalScheduledTime}</small>`
                  : ""
                }`
              : "-"
            }</td>`;
           }
           if (field === "status") {
            return `<td style="max-width: 70px;">${
             this.getDisplayStatusMeta(appointment).label
            }</td>`;
           }
           if (field === "ageSex") {
            const age = appointment.patientAge || "-";
            const sex = appointment.patientGender || "-";
            return `<td style="text-align: center; max-width: 40px;"><strong>${age} / ${sex}</strong></td>`;
           }
           if (field === "eye") {
            return `<td style="max-width: 40px;"><strong>${
             appointment.eye || "-"
            }</strong></td>`;
           }
           if (field === "lens") {
            return `<td style="max-width: 80px;">${
             appointment.lens || "NO LENS"
            }</td>`;
           }
           if (field === "surgeryName") {
            const anesthesia = appointment.anesthesia || "-";
            const surgery =
             appointment.surgeryName || appointment.surgery || "-";
            return `<td style="max-width: 200px;"><div class="anesthesia-text">${anesthesia}</div><hr style="border:none; border-top:1px solid #ccc; margin:2px 0;"/><div>${surgery}</div></td>`;
           }
           if (field === "surgeryGroup") {
            return `<td style="max-width: 100px;">${
             appointment.surgeryGroup || "-"
            }</td>`;
           }
           if (field === "diet") {
            return `<td style="max-width: 80px;">${
             appointment.diet || "-"
            }</td>`;
           }
           if (field === "postponedAt") {
            return `<td style="max-width: 80px;">${
             appointment.postponedAt
              ? moment(appointment.postponedAt).format("DD/MM/YY hh:mm A")
              : "-"
            }</td>`;
           }
           if (field === "postponedBy") {
            return `<td style="max-width: 80px;">${
             appointment.postponedBy || "-"
            }</td>`;
           }
           if (field === "remarks") {
            return `<td style="min-width: 80px; max-width: 150px;">${
             appointment.remarks || "-"
            }</td>`;
           }

           // Default: return field value or dash
           return `<td>${appointment[field] || "-"}</td>`;
          })
          .join("");
         return `<tr class="no-break">${cells}</tr>`;
        })
        .join("")}
      </tbody>
     </table>
    </body>
    </html>
   `;

   printWindow.document.write(htmlContent);
   printWindow.document.close();

   // Wait for content to load then trigger print
   printWindow.onload = () => {
    printWindow.print();
   };

   cogoToast.success("PDF print dialog opened (with selected columns)");
  } catch (err) {
   console.error("Error exporting PDF:", err);
   cogoToast.error("Failed to export PDF");
  }
 };

 renderReportHeader = () => {
  const selectedBranchName =
   this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name ||
   "All Branches";
  const selectedDoctorName = this.state.selectedDoctorName
   ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
   : null;

  return (
   <div
    style={{
     textAlign: "center",
     marginBottom: "30px",
     borderBottom: "2px solid var(--border-color)",
     paddingBottom: "20px",
    }}
   >
    <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
     POSTPONED SURGERY APPOINTMENT REGISTER - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
    </H1>
    <Text
     style={{
      color: "var(--text-secondary)",
      margin: "5px 0",
      fontSize: "16px",
     }}
    >
     {selectedBranchName}
     {selectedDoctorName ? ` • ${selectedDoctorName}` : ""} -{" "}
     {moment().format("DD/MM/YYYY, HH:mm")}
    </Text>
     {this.state.dateFilterApplied && this.state.fromDate && this.state.toDate && (
      <Text
       style={{
        color: "var(--text-secondary)",
        margin: "5px 0",
        fontSize: "14px",
       }}
      >
       Surgeries scheduled for {moment(this.state.fromDate).format("DD-MMM-YYYY")}{" "}
       to {moment(this.state.toDate).format("DD-MMM-YYYY")}
      </Text>
     )}
     {!this.state.dateFilterApplied && (
      <Text
       style={{
        color: "var(--text-secondary)",
        margin: "5px 0",
        fontSize: "14px",
       }}
      >
       Showing all postponed surgeries (no date filter applied)
      </Text>
     )}
   </div>
  );
 };

 renderFilters = () => {
  return (
   <Card
    elevation={2}
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "15px",
     }}
    >
     <Icon
      icon="filter"
      style={{ marginRight: "10px", color: "var(--text-primary)" }}
     />
     <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Report Filters</H3>
     <div
      style={{
       marginLeft: "auto",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="success" large>
       {this.state.appointments.length} Appointments
      </Tag>
      {/* <Tag intent="primary" large>
       {Object.keys(this.state.groupedData).length} Doctors
      </Tag> */}
     </div>
    </div>

    <div
     style={{
      display: "flex",
      alignItems: "center",
      gap: "15px",
      flexWrap: "nowrap",
      marginBottom: "15px",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       From Date
      </Tag>
      <DateInput
       value={this.state.fromDate}
       onChange={(date) => this.handleDateChange("fromDate", date)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="Select From Date (Optional)"
       style={{ width: "180px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       To Date
      </Tag>
      <DateInput
       value={this.state.toDate}
       onChange={(date) => this.handleDateChange("toDate", date)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="Select To Date (Optional)"
       style={{ width: "180px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Branch
      </Tag>
      <div style={{ width: "160px" }}>
       <SingleSelectWrapper
        items={[
         `All Branches (${this.state.branches.length})`,
         ...this.createBranchSelectItems(),
        ]}
        selectedItem={
         this.state.selectedBranchName ||
         `All Branches (${this.state.branches.length})`
        }
        placeholder="Search branches..."
        onChange={(value) => {
         if (value === `All Branches (${this.state.branches.length})`) {
          this.handleBranchFilterSelect(null);
         } else {
          this.handleBranchFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Doctor/Surgeon
      </Tag>
      <div style={{ width: "180px" }}>
       <SingleSelectWrapper
        items={[
         `All Doctors (${this.state.doctors.length})`,
         ...this.createDoctorSelectItems(),
        ]}
        selectedItem={
         this.state.selectedDoctorName ||
         `All Doctors (${this.state.doctors.length})`
        }
        placeholder="Search doctors..."
        onChange={(value) => {
         if (value === `All Doctors (${this.state.doctors.length})`) {
          this.handleDoctorFilterSelect(null);
         } else {
          this.handleDoctorFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>
    </div>

    <div
     style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "15px",
      flexWrap: "nowrap",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Quick Filter
      </Tag>
      <InputGroup
       placeholder="Search all columns..."
       value={this.state.quickFilterText}
       onChange={this.onQuickFilterChanged}
       leftIcon="search"
       style={{ width: "200px" }}
       small
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Button
       intent="primary"
       icon="search"
       onClick={this.generateReport}
       loading={this.state.isGenerating}
      >
       Generate
      </Button>

      <Button
       icon="document"
       onClick={this.exportToPDF}
       disabled={this.state.appointments.length === 0}
       title="Export to PDF"
      >
       PDF
      </Button>

      <Button
       icon="th"
       onClick={this.exportToCSV}
       disabled={this.state.appointments.length === 0}
       title="Export to CSV"
      >
       CSV
      </Button>
     </div>
    </div>
   </Card>
  );
 };

 renderAppointmentTable = (appointments, doctorName) => {
  if (!this.state.appointments || this.state.appointments.length === 0) {
   return (
    <div
     style={{
      textAlign: "center",
      padding: "40px",
      color: "var(--text-secondary)",
      backgroundColor: "var(--bg-secondary)",
      borderRadius: "8px",
      border: "1px solid var(--border-color)",
     }}
    >
     <Icon
      icon="calendar"
      size={48}
      style={{ marginBottom: "15px", opacity: 0.5 }}
     />
     <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
      No appointments found
     </div>
     <div style={{ fontSize: "14px", opacity: 0.8 }}>
      Try adjusting your date range or filters
     </div>
    </div>
   );
  }

  return (
   <div
    className="ag-theme-alpine-dark"
    style={{
     height: "600px",
     width: "100%",
     borderRadius: "8px",
     border: "1px solid var(--border-color)",
     backgroundColor: "var(--bg-surface)",
    }}
   >
    <AgGridReact
     rowData={this.state.appointments}
     columnDefs={this.getColumnDefs()}
     onGridReady={this.onGridReady}
     quickFilterText={this.state.quickFilterText}
     animateRows={true}
     rowSelection="multiple"
     suppressRowClickSelection={true}
     enableBrowserTooltips={true}
     enableRangeSelection={true}
     enableCellTextSelection={true}
     sideBar={{
      toolPanels: [
       {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
         suppressRowGroups: true,
         suppressValues: true,
         suppressPivots: true,
         suppressPivotMode: true,
         suppressColumnFilter: false,
         suppressColumnSelectAll: false,
         suppressColumnExpandAll: false,
        },
       },
       {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
        toolPanelParams: {
         suppressExpandAll: false,
         suppressFilterSearch: false,
        },
       },
      ],
      defaultToolPanel: null, // No panel open by default
      hiddenByDefault: false,
      position: "right",
     }}
     defaultColDef={{
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 80,
      menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
     }}
     pagination={true}
     paginationPageSize={50}
     paginationPageSizeSelector={[25, 50, 100, 200]}
     suppressPaginationPanel={false}
    domLayout="normal"
     getRowId={(params) => params.data._id || params.data.surgeryId}
    getRowHeight={this.getRowHeight}
     headerHeight={45}
    />
   </div>
  );
 };

 renderReportContent = () => {
  if (this.state.isGenerating) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Generating Report..."
     description="Please wait while we fetch the appointment data"
    />
   );
  }

  if (this.state.appointments.length === 0) {
   return (
    <NonIdealState
     icon="calendar"
     title="No Appointments Found"
     description="No appointments found for the selected criteria. Try adjusting your filters."
    />
   );
  }

  return (
   <div>
    {this.renderReportHeader()}

    {Object.keys(this.state.groupedData).map((groupName) => (
     <div key={groupName} style={{ marginBottom: "30px" }}>
      {this.renderAppointmentTable(
       this.state.groupedData[groupName],
       groupName
      )}
     </div>
    ))}
   </div>
  );
 };

 renderInfoPanel = () => {
  return (
   <Card
    elevation={1}
    style={{
     padding: "15px",
     backgroundColor: "var(--bg-secondary)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "10px",
     }}
    >
     <Icon
      icon="info-sign"
      style={{ marginRight: "8px", color: "var(--intent-primary)" }}
     />
     <Text
      style={{
       color: "var(--text-primary)",
       margin: 0,
       fontSize: "14px",
       fontWeight: "600",
      }}
     >
      Quick Guide: Select date range, filter by branch/doctor, use quick filter
      to search all columns, click column headers to sort, right-click for
      advanced filtering
     </Text>
    </div>
   </Card>
  );
 };

 render() {
  if (this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Loading..."
     description="Preparing filters and data"
    />
   );
  }
  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
   >
    <div style={{ marginBottom: "20px" }}>
     <div style={{ display: "flex", alignItems: "center" }}>
      <Icon
       icon="list-detail-view"
       style={{ marginRight: "12px", color: "var(--intent-primary)" }}
      />
      <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
       Postponed Appointment Register
      </H1>
     </div>
    </div>

    {/* {this.renderInfoPanel()} */}
    {this.renderFilters()}

    <Card
     elevation={2}
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-surface)",
      border: "1px solid var(--border-color)",
     }}
    >
     {this.renderReportContent()}
    </Card>
   </div>
  );
 }
}

export default inject("authStore")(observer(withRouter(PostponedRegister)));
